'use client';

import React, { useState, useEffect } from 'react';
import { FileUp, CreditCard, Rocket } from 'lucide-react';
import { buttonVariants } from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';

const HowTo: React.FC = () => {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const { ref, isVisible } = useIntersectionObserver();
  
  useEffect(() => {
    // Mark as mounted after hydration is complete
    setMounted(true);
  }, []);

  // Server-rendered fallback, shown before hydration — i.e. this IS what
  // crawlers and first paint see. It drifted badly: it used to describe a
  // one-off "pay a small fee to ensure your pitch deck is distributed" model
  // with guaranteed distribution, which is not the product (monthly
  // subscription, AI matching, user approves every send). Keep it in sync with
  // pageContent.home.howTo.steps.* or it silently becomes the version Google
  // indexes.
  const staticSteps = [
    {
      icon: <FileUp className="w-12 h-12 text-primary-500" />,
      title: 'Upload Your Pitch Deck',
      description: 'Upload your pitch deck in PDF format with a brief description of your startup.'
    },
    {
      icon: <CreditCard className="w-12 h-12 text-primary-500" />,
      title: 'Subscribe',
      description: 'Choose a plan from $19/month. No commission, no equity, cancel anytime.'
    },
    {
      icon: <Rocket className="w-12 h-12 text-primary-500" />,
      title: 'Reach Investors',
      description: 'AI matches your deck to investors and writes a personal email for each. You approve every send.'
    }
  ];
  
  // Use translations only after client-side hydration
  const steps = mounted ? [
    {
      icon: <FileUp className="w-12 h-12 text-primary-500" />,
      title: t('pageContent.home.howTo.steps.upload.title'),
      description: t('pageContent.home.howTo.steps.upload.description')
    },
    {
      icon: <CreditCard className="w-12 h-12 text-primary-500" />,
      title: t('pageContent.home.howTo.steps.pay.title'),
      description: t('pageContent.home.howTo.steps.pay.description')
    },
    {
      icon: <Rocket className="w-12 h-12 text-primary-500" />,
      title: t('pageContent.home.howTo.steps.connect.title'),
      description: t('pageContent.home.howTo.steps.connect.description')
    }
  ] : staticSteps;

  return (
    <section id="how-it-works" className="py-16 md:py-24">
      <div className="container max-w-5xl">
        <div ref={ref} className={`text-center mb-16 fade-in-section ${isVisible ? 'is-visible' : ''}`}>
          <h2 
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
            suppressHydrationWarning
          >
            {mounted ? t('pageContent.home.howTo.title') : 'Three Simple Steps'}
          </h2>
          <p 
            className="text-xl text-gray-600"
            suppressHydrationWarning
          >
            {mounted ? t('pageContent.home.howTo.subtitle') : 'Get your pitch deck in front of the right investors with our easy process'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className={`flex flex-col items-center text-center relative fade-in-section ${isVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${index * 0.2}s` }}
            >
              <div className="bg-primary-50 p-6 rounded-2xl mb-6 relative z-10">
                <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-primary-600 text-white text-sm font-bold flex items-center justify-center shadow-md">
                  {index + 1}
                </span>
                {/* Icon only illustrates the step title below it — hide from AT */}
                <div className="animate-icon-pulse hover-scale" style={{ animationDelay: `${index}s` }} aria-hidden="true">
                  {step.icon}
                </div>
              </div>
              <div>
                <h3 
                  className="text-xl font-bold text-gray-900 mb-2"
                  suppressHydrationWarning
                >
                  {step.title}
                </h3>
                <p 
                  className="text-gray-600"
                  suppressHydrationWarning
                >
                  {step.description}
                </p>
              </div>
              
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute right-0 top-1/2 w-full transform -translate-y-1/2 translate-x-1/2 z-0">
                  <div 
                    className={`h-0.5 bg-primary-100 w-full origin-left ${isVisible ? 'animate-scale-x' : ''}`}
                    style={{ animationDelay: `${0.5 + index * 0.2}s` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={`mt-16 text-center fade-in-section ${isVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '0.8s' }}>
          <a
            href="#upload-section"
            className={buttonVariants({ size: 'lg', className: 'hover-scale' })}
            suppressHydrationWarning
          >
            {mounted ? t('pageContent.home.howTo.cta') : 'Get Started Now'}
          </a>
        </div>
      </div>
    </section>
  );
};

export { HowTo };
